import { FC, useEffect, useMemo } from 'react'

import { Box, Button, HStack, Icon, IconButton, Link, Text, VStack, useDisclosure } from '@chakra-ui/react'
import { CloseIcon } from '@editor/assets/icons'
import TutorialVideoModal from '@editor/components/TutorialVideoModal'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'

import { AttentionTextIcon, closeAttentionText, reset } from './store/attentionText'

/**
 * Shows a guide text on the top of the page.
 * The usage is pretty simple, just set the message in attentionText store (setAttentionText).
 */
const AttentionText: FC = () => {
  // Store
  const dispatch = useAppDispatch()
  const message = useSelector((state: RootState) => state.attentionText.message)
  const icon = useSelector((state: RootState) => state.attentionText.icon)
  const linkMessage = useSelector((state: RootState) => state.attentionText.linkMessage)
  const linkUrl = useSelector((state: RootState) => state.attentionText.linkUrl)
  const tutorialVideo = useSelector((state: RootState) => state.attentionText.tutorialVideo)
  const isClosedByUser = useSelector((state: RootState) => state.attentionText.isClosedByUser)
  const videos = useSelector((root: RootState) => root.videos.videos)

  // Video modal
  const useDisclosureProps = useDisclosure()
  const videoConfig = useMemo(() => {
    if (tutorialVideo?.variant) {
      return videos.find(
        (video) => video.toolType === tutorialVideo?.toolType && video.variant === tutorialVideo?.variant,
      )
    }
    return videos.find((video) => video.toolType === tutorialVideo?.toolType && !video.variant)
  }, [videos, tutorialVideo])

  // Derived
  const AttentionIcon = useMemo(() => (icon ? AttentionTextIcon[icon] : null), [icon])

  /**
   * Reset on unmount
   */
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch],
  )

  return message && !isClosedByUser ? (
    <Box id="attention-text-container" position="absolute" left="50%" transform="translateX(-50%)">
      <Box
        id="attention-text"
        position="relative"
        textAlign="center"
        minWidth={200}
        background="whiteAlpha.900"
        color="gray2.800"
        p="20px"
        borderRadius={5}
        whiteSpace="pre-line"
      >
        <HStack gap={3} align="start">
          {AttentionIcon && (
            <Icon w={14} h={14} color="gray2.800" asChild>
              <AttentionIcon />
            </Icon>
          )}
          <VStack flex={1}>
            <Text>{message}</Text>
            {linkMessage && linkUrl && (
              <Link variant="underline" href={linkUrl} target="_blank" rel="noopener noreferrer">
                {linkMessage}
              </Link>
            )}
            {tutorialVideo && videoConfig && (
              <>
                <Button variant="plain" colorPalette="blue" onClick={() => useDisclosureProps.onOpen()}>
                  {tutorialVideo.linkMessage}
                </Button>
                <TutorialVideoModal
                  useDisclosureProps={useDisclosureProps}
                  {...videoConfig}
                  title={tutorialVideo.title || videoConfig.title}
                />
              </>
            )}
          </VStack>
        </HStack>
        <IconButton
          aria-label="Close instructions"
          size="xs"
          variant="ghost"
          color="gray2.500"
          onClick={() => dispatch(closeAttentionText())}
          _hover={{ color: 'gray2.700' }}
          position="absolute"
          top={1}
          right={1}
          minWidth="auto"
          width={5}
          height={5}
          borderRadius="full"
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  ) : null
}

export default AttentionText
